/**
 * Real-time Lip Sync Integration Example
 * Based on the RealtimeLLMToElevenLabs.js + LipSyncController.minimal.js guide
 * 
 * This demonstrates the exact integration pattern for real-time audio-driven lip sync
 */

import { RealtimeLLMToElevenLabs } from "../modules/RealtimeLLMToElevenLabs.js";
import { LipSyncController } from "../modules/LipSyncController.minimal.js";

export class RealtimeLipSyncIntegration {
    constructor(characterMesh, elevenLabsConfig) {
        this.characterMesh = characterMesh;
        this.elevenLabsConfig = elevenLabsConfig;
        this.tts = null;
        this.lips = null;
        this.isInitialized = false;
    }

    /**
     * Initialize the real-time lip sync system
     * Call this after a user gesture to avoid browser audio blocking
     */
    async initialize() {
        console.log("🎤 Initializing real-time lip sync system...");

        // 1) Prepare your mesh (using minimal controller)
        this.lips = new LipSyncController({ model: this.characterMesh });
        console.log("✓ LipSync controller initialized with character mesh");

        // 2) Create and configure streaming TTS
        this.tts = new RealtimeLLMToElevenLabs({
            voiceId: this.elevenLabsConfig.voiceId,
            elevenLabsApiKey: this.elevenLabsConfig.apiKey,
            outputFormat: "pcm_16000",
            phraseMaxTokens: 18,
            phraseMaxWaitMs: 500,
            jitterMs: 120,
        });
        console.log("✓ RealtimeLLMToElevenLabs configured");

        // 3) Enable real-time lipsync (before playback starts)
        this.tts.enableLipSync(this.lips, {
            frameMs: 33,      // analysis cadence (~30 fps)
            minRms: 0.015,    // silence threshold → MBP
            hissRatio: 1.35,  // sibilants/fricatives sensitivity
            fftSize: 1024,
        });
        console.log("✓ Real-time lip sync enabled");

        // 4) Start the ElevenLabs WebSocket
        await this.tts.start();
        console.log("✓ ElevenLabs WebSocket connected");

        this.isInitialized = true;
        console.log("🎤 Real-time lip sync system ready!");
    }

    /**
     * Test the lip sync with a simple phrase
     */
    async testLipSync() {
        if (!this.isInitialized) {
            throw new Error("System not initialized. Call initialize() first.");
        }

        console.log("🧪 Testing lip sync...");
        
        // Test immediate phoneme application
        console.log("Testing direct phoneme application...");
        
        // Should open the mouth:
        this.lips.applyPhoneme("AAA", 0.9);
        console.log("✓ Applied 'AAA' phoneme");
        
        // After 500ms, should close:
        setTimeout(() => {
            this.lips.applyPhoneme("MBP", 1.0);
            console.log("✓ Applied 'MBP' phoneme (closed mouth)");
        }, 500);

        // Test with streaming tokens
        setTimeout(async () => {
            console.log("Testing streaming audio with lip sync...");
            this.handleLLMToken("Hi there, ");
            await new Promise(r => setTimeout(r, 200));
            this.handleLLMToken("this is a real-time ");
            await new Promise(r => setTimeout(r, 200));
            this.handleLLMToken("streamed sentence with lip sync.");
            await new Promise(r => setTimeout(r, 500));
            this.finishStream();
        }, 1000);
    }

    /**
     * Process LLM tokens for real-time TTS
     */
    handleLLMToken(delta) {
        if (!this.isInitialized) {
            console.warn("System not initialized. Ignoring token:", delta);
            return;
        }
        
        console.log("[LLM] Processing token:", delta);
        this.tts.handleLLMDelta(delta);
    }

    /**
     * Signal end of LLM stream
     */
    finishStream() {
        if (!this.isInitialized) {
            console.warn("System not initialized. Cannot finish stream.");
            return;
        }
        
        console.log("🏁 Finishing LLM stream...");
        this.tts.flushAndClose();
    }

    /**
     * Cancel current audio mid-stream
     */
    cancel() {
        if (!this.isInitialized) {
            console.warn("System not initialized. Cannot cancel.");
            return;
        }
        
        console.log("❌ Cancelling current stream...");
        this.tts.cancel();
    }

    /**
     * Get system status
     */
    getStatus() {
        return {
            initialized: this.isInitialized,
            supportedPhonemes: this.lips?.getSupportedPhonemes() || [],
            ttsConnected: this.tts?.wsOpen || false,
            lipSyncEnabled: this.tts?._wantLipSync || false
        };
    }

    /**
     * Cleanup resources
     */
    cleanup() {
        if (this.tts) {
            this.tts.cancel();
        }
        if (this.lips) {
            this.lips.reset();
        }
        this.isInitialized = false;
        console.log("🧹 Real-time lip sync system cleaned up");
    }
}

/**
 * Quick setup function for easy integration
 */
export async function setupRealtimeLipSync(characterMesh, elevenLabsConfig) {
    const integration = new RealtimeLipSyncIntegration(characterMesh, elevenLabsConfig);
    await integration.initialize();
    return integration;
}

/**
 * Integration example with ModelViewer
 * This shows how to integrate with your existing 3D character system
 */
export class ModelViewerLipSyncIntegration {
    constructor(modelViewer, elevenLabsConfig) {
        this.modelViewer = modelViewer;
        this.elevenLabsConfig = elevenLabsConfig;
        this.realtimeLipSync = null;
    }

    async initialize() {
        // Wait for model to be loaded
        if (!this.modelViewer.currentModel) {
            console.log("⏳ Waiting for 3D model to load...");
            await new Promise(resolve => {
                const checkModel = () => {
                    if (this.modelViewer.currentModel) {
                        resolve();
                    } else {
                        setTimeout(checkModel, 100);
                    }
                };
                checkModel();
            });
        }

        // Get the character mesh from ModelViewer
        const characterMesh = this.modelViewer.currentModel;
        console.log("✓ Character mesh ready:", characterMesh);

        // Initialize real-time lip sync
        this.realtimeLipSync = new RealtimeLipSyncIntegration(characterMesh, this.elevenLabsConfig);
        await this.realtimeLipSync.initialize();

        // Connect to existing chat system if available
        this.connectToChatSystem();

        console.log("🎭 ModelViewer real-time lip sync integration complete!");
        return this.realtimeLipSync;
    }

    connectToChatSystem() {
        // This would connect to your existing ChatController
        // Example: listening for LLM streaming responses
        console.log("🔗 Connecting to existing chat system...");
        
        // You would replace this with actual integration:
        // if (window.chatController) {
        //     window.chatController.onLLMDelta = (delta) => {
        //         this.realtimeLipSync.handleLLMToken(delta);
        //     };
        //     window.chatController.onLLMComplete = () => {
        //         this.realtimeLipSync.finishStream();
        //     };
        // }
    }

    getStatus() {
        return {
            modelLoaded: !!this.modelViewer.currentModel,
            lipSyncReady: !!this.realtimeLipSync,
            ...this.realtimeLipSync?.getStatus()
        };
    }
}